import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * "There is nothing here", as the design draws it.
 *
 * Transcribed rather than invented: 72px of vertical air, a 60px rounded
 * square holding a 26px icon, a 15.5px title, a 13px line capped at 360px so
 * it breaks onto two readable lines, and the actions as a filled/outline pair.
 *
 * ── Why this is a component and not a sentence in a table cell ──────────────
 *
 * An empty table is not an error and it is not a loading state - it is a
 * screen with one obvious next move, and the whole job of this block is to
 * put that move in front of the person looking at it. A single line of grey
 * text in the middle of a bordered table says "nothing here" and stops, which
 * leaves an admin opening a menu to find out how to add the first one.
 *
 * Deliberately separate from `ErrorState`: that one is `role="alert"` and
 * announces itself, because something went wrong. Nothing has gone wrong
 * here, and a screen reader being interrupted to say so is a bug.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  actions,
  className,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  /** Filled primary first, outline second - the design's pairing. */
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn('flex flex-col items-center px-8 py-18 text-center', className)}
      data-slot="empty-state"
    >
      <div className="mb-4.5 flex size-15 items-center justify-center rounded-2xl bg-muted">
        <Icon size={26} strokeWidth={1.6} className="text-neutral-400" aria-hidden="true" />
      </div>

      <p className="mb-1.75 text-[15.5px] font-semibold">{title}</p>
      <p className="mb-5 max-w-90 text-[13px] leading-[1.6] text-pretty text-muted-foreground">
        {description}
      </p>

      {actions && (
        <div className="flex flex-wrap items-center justify-center gap-2.25">{actions}</div>
      )}
    </div>
  );
}
